import { type RefObject, useEffect, useState } from "react";

export type DropdownPlacement = "down" | "up";

/**
 * Decide whether a dropdown panel opens below ("down") or flips above
 * ("up") its trigger — exactly like a native `<select>`: prefer down,
 * flip up only when the panel can't fit below the trigger but fits
 * better above. Without this, panels anchored to fixed-position bars
 * (e.g. the time-entries bulk-action bar pinned to the bottom of the
 * viewport) open off the bottom edge of the screen.
 *
 * Measures the trigger on open and keeps it correct on scroll / resize
 * while the panel is open. Pass the ref of the element the panel is
 * positioned against (the `relative` wrapper — its border box stays the
 * trigger's size because the panel is absolutely positioned out of flow).
 */
export function useDropdownPlacement(
  triggerRef: RefObject<HTMLElement | null>,
  open: boolean,
  estimatedPanelHeight = 280,
): DropdownPlacement {
  const [placement, setPlacement] = useState<DropdownPlacement>("down");

  useEffect(() => {
    if (!open) {
      // Reset so the next open re-evaluates from the default-down state
      // rather than inheriting a stale "up" from a previous position.
      setPlacement("down");
      return;
    }
    function measure() {
      const el = triggerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      // Stay down unless below genuinely can't hold the panel AND there's
      // more room above — never flip up when down still has more space.
      setPlacement(spaceBelow < estimatedPanelHeight && spaceAbove > spaceBelow ? "up" : "down");
    }
    measure();
    window.addEventListener("resize", measure);
    // Capture phase so scrolls inside any ancestor scroll container also
    // re-measure, not just the window.
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [open, triggerRef, estimatedPanelHeight]);

  return placement;
}
